import { useApi } from "@utils/useApi";
import {
  AccountAdminClient,
  AccountContractRequest,
  AccountContractResponse,
  ContractClient,
  ContractRetainerSpend,
  ContractRetainerSpendClient,
  ContractSLA,
  ContractsListFilterDto,
  IAccountContractRequest,
  IContractDto,
  ProjectClient,
  ProjectListItem,
  ProjectListResponse,
  AccountResponse as ApiAccountResponse,
  Account as ApiAccount,
} from "@lib/ShiOneClient";
import { useQueries, useQuery, UseQueryResult } from "@tanstack/react-query";
import {
  calculateContractDuration,
  calculateMonthsBetweenDates,
  getBillingTypeLabel,
} from "@features/services-feature/contracts/functions";
import { parseDateOperatorValues } from "shared-ui/src/utils/filterDateOperatorUtils";
import _ from "lodash";
import {
  AccountResponse as SharedAccountResponse,
  Account as SharedAccount,
} from "shared-ui/src/features/contracts-feature/canonical";
import type { AccountContractResponse as SharedAccountContractResponse } from "shared-ui/src/features/contracts-feature/canonical/contract.types";

function mapApiAccountToSharedAccount(apiAccount: ApiAccount): SharedAccount {
  return { ...apiAccount } as SharedAccount;
}

function mapApiAccountResponseToSharedAccountResponse(
  apiResponse: ApiAccountResponse
): SharedAccountResponse {
  return {
    responseStatus: apiResponse.responseStatus,
    errorMessage: apiResponse.errorMessage,
    accounts: apiResponse.accounts?.map(mapApiAccountToSharedAccount),
  };
}

export function useGetAccounts() {
  const api = useApi(AccountAdminClient);

  return useQuery<SharedAccountResponse, Error>({
    queryKey: ["accounts"],
    queryFn: async () => {
      const apiResponse = await api.getAccounts();
      return mapApiAccountResponseToSharedAccountResponse(apiResponse);
    },
  });
}

function getAccountIdsFromAccounts(accounts: SharedAccount[] | undefined) {
  if (!accounts) {
    return [];
  }

  return accounts.map((a) => a.id!);
}

type IncludeExcludeFilter = {
  include: Record<string, string[]>;
  exclude: Record<string, string[]>;
};

function buildCXFilterDto(
  filterMap: Record<string, string[]>,
): ContractsListFilterDto | undefined {
  if (!filterMap || Object.keys(filterMap).length === 0) return undefined;

  const dto = new ContractsListFilterDto();

  if (filterMap.account?.length) {
    dto.accountName = filterMap.account;
  }
  if (filterMap.status?.length) {
    dto.status = filterMap.status;
  }
  if (filterMap.billingType?.length) {
    dto.billingTypeLabel = filterMap.billingType;
  }
  if (filterMap.contractType?.length) {
    dto.serviceContractTypeName = filterMap.contractType;
  }
  if (filterMap.startDate?.length) {
    const parsed = parseDateOperatorValues(filterMap.startDate);
    if (parsed.after) dto.startDateAfter = parsed.after;
    if (parsed.before) dto.startDateBefore = parsed.before;
  }
  if (filterMap.endDate?.length) {
    const parsed = parseDateOperatorValues(filterMap.endDate);
    if (parsed.after) dto.endDateAfter = parsed.after;
    if (parsed.before) dto.endDateBefore = parsed.before;
  }

  const hasValues =
    dto.accountName?.length ||
    dto.status?.length ||
    dto.billingTypeLabel?.length ||
    dto.serviceContractTypeName?.length ||
    dto.startDateAfter ||
    dto.startDateBefore ||
    dto.endDateAfter ||
    dto.endDateBefore;

  return hasValues ? dto : undefined;
}

function useGetAccountContracts({
  accounts,
  includeExcludeFilter,
  search,
}: {
  accounts: SharedAccount[] | undefined;
  includeExcludeFilter?: IncludeExcludeFilter;
  search?: string;
}) {
  const activeAccountIds = getAccountIdsFromAccounts(accounts);

  const searchFilterInclude = includeExcludeFilter
    ? buildCXFilterDto(includeExcludeFilter.include)
    : undefined;
  const searchFilterExclude = includeExcludeFilter
    ? buildCXFilterDto(includeExcludeFilter.exclude)
    : undefined;

  const request: IAccountContractRequest = {
    accounts: activeAccountIds,
    pageSize: 300,
    pageNumber: 1,
    searchString: search || undefined,
    searchFilterInclude,
    searchFilterExclude,
  };

  const api = useApi(ContractClient);

  return useQuery({
    queryKey: [
      "account-contracts",
      activeAccountIds,
      includeExcludeFilter,
      search,
    ],
    queryFn: () =>
      api.getAccountContracts(true, request as AccountContractRequest),
    enabled: accounts && accounts.length > 0,
  });
}

function useGetProjects() {
  const api = useApi(ProjectClient);

  return useQuery({
    queryKey: ["projects"],
    queryFn: () => api.getProjectList(),
  });
}

function useGetContractSLAs() {
  const api = useApi(ContractClient);

  return useQuery({
    queryKey: ["slas"],
    queryFn: () => api.getSLAs(),
  });
}

export const useGetContractRetainersByAccountIds = (accountIds: number[]) => {
  const api = useApi(ContractRetainerSpendClient);
  return useQueries({
    queries: accountIds.map((accountId) => {
      return {
        queryKey: ["contractById", accountId],
        queryFn: () => api.getContractRetainerSpendByAccountId(accountId),
      };
    }),
  });
};

export type ExtendedContract = IContractDto & {
  projectCount: number;
  contractTypeName: string | undefined;
  billingTypeLabel: string | undefined;
  accountName: string | undefined;
  projectDuration: number | undefined;
  projectDurationLabel: string | undefined;
  retainers: ContractRetainerSpend[] | undefined;
};

function useResolveContractData(
  retainersTransformed: ContractRetainerSpend[],
  contractResponse: UseQueryResult<AccountContractResponse>,
  projectResponse: UseQueryResult<ProjectListResponse>,
  slaResponse: UseQueryResult<ContractSLA[]>,
  accounts: SharedAccount[] | undefined,
  picklist: any,
): ExtendedContract[] {
  if (
    !retainersTransformed ||
    !contractResponse.data?.resultContracts ||
    !projectResponse.data?.projects ||
    !slaResponse.data
  ) {
    return [];
  }

  return contractResponse.data.resultContracts.map(
    (contract: IContractDto): ExtendedContract => {
      const projectCount = projectResponse.data.projects!.filter(
        (project: ProjectListItem) => project.contractID === contract.id,
      ).length;

      // Resolve the sla for the contract
      const sla = slaResponse.data?.find(
        (sla) => sla.id === contract.serviceLevelAgreementID,
      );

      const retainers = retainersTransformed.filter(
        (retainer) => retainer.contractId === contract.id,
      );

      const contractTypeName = sla?.contractType || "Other";

      const billingTypeLabel = getBillingTypeLabel(
        contract.contractType,
        picklist,
      );

      const accountName = _.find(
        accounts,
        (a) => a.id === contract.accountID,
      )?.accountName;

      let projectDuration: number | undefined = calculateContractDuration(
        contract.startDate,
        contract.endDate,
      );

      const contractLengthInMonths = calculateMonthsBetweenDates(
        contract.startDate!,
        contract.endDate!,
      );

      let projectDurationLabel = undefined;

      if (contractLengthInMonths > 120) {
        projectDuration = undefined;
      } else {
        let monthsFromStartOfContract = calculateMonthsBetweenDates(
          contract.startDate!,
          new Date(),
        );

        projectDurationLabel = `${Math.min(
          monthsFromStartOfContract,
          contractLengthInMonths,
        )} / ${contractLengthInMonths} months (${projectDuration}%)`;
      }

      return {
        ...contract,
        retainers,
        projectCount,
        contractTypeName,
        billingTypeLabel,
        accountName,
        projectDuration,
        projectDurationLabel,
      };
    },
  );
}

export function useGetContractData({
  accounts,
  picklist,
  includeExcludeFilter,
  search,
}: {
  accounts: SharedAccount[] | undefined;
  picklist: any;
  includeExcludeFilter?: IncludeExcludeFilter;
  search?: string;
}): SharedAccountContractResponse {
  const accountContractsResponse = useGetAccountContracts({
    accounts,
    includeExcludeFilter,
    search,
  });

  const projectsResponse = useGetProjects();
  const slaResponse = useGetContractSLAs();

  const activeAccounts = getAccountIdsFromAccounts(accounts);
  const contractsRetainerSpendResponse =
    useGetContractRetainersByAccountIds(activeAccounts);

  const retainersTransformed = contractsRetainerSpendResponse.reduce(
    (accumulator: ContractRetainerSpend[], retainer) => {
      return accumulator.concat(retainer.data ? retainer.data : []);
    },
    [],
  );

  return {
    isLoading:
      accountContractsResponse.isLoading ||
      projectsResponse.isLoading ||
      slaResponse.isLoading,
    error:
      accountContractsResponse.error ||
      projectsResponse.error ||
      slaResponse.error,
    gridData: useResolveContractData(
      retainersTransformed,
      accountContractsResponse,
      projectsResponse,
      slaResponse,
      accounts,
      picklist,
    ),
    resultInfo: accountContractsResponse.data?.resultInfo,
  } as SharedAccountContractResponse;
}
